import { Skeleton } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import { useEffect, useRef } from "react"
import InfiniteScroll from "react-infinite-scroll-component"

import { type ProjectListItem } from "../../types/types.ts"
import { ProjectGridCard } from "./ProjectGridCard.tsx"

export const projectListGridClassName =
  "grid gap-4 grid-cols-[repeat(auto-fill,minmax(250px,1fr))] 2xl:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]"

interface ProjectListGridProps {
  projects: ProjectListItem[]
  hasMore: boolean
  statusEditable: boolean
  photoEditable: boolean
  showProgress: boolean
  showBudget: boolean
  deleteAllowed?: boolean
  statuses: BaseOptionType[]
  isLoading?: boolean
  onLoadMoreProjects: () => void
  onProjectClick: (id: string) => void
  onDeleteProject?: (id: string) => void
  onStatusChange: (id: string, status: number) => Promise<void>
  onUpdateMainPhoto: (
    projectId: string,
    file?: File | null
  ) => Promise<string | null>
}

export function ProjectListGrid({
  projects,
  hasMore,
  statuses,
  statusEditable,
  photoEditable,
  showProgress,
  showBudget,
  deleteAllowed,
  isLoading,
  onLoadMoreProjects,
  onProjectClick,
  onDeleteProject,
  onStatusChange,
  onUpdateMainPhoto
}: ProjectListGridProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Fetch data if the content doesn't fill the screen
  useEffect(() => {
    if (!hasMore || isLoading) return

    const container = containerRef.current
    if (!container) return

    const hasNoScroll = container.scrollHeight <= container.clientHeight
    if (hasNoScroll && projects.length > 0) {
      onLoadMoreProjects()
    }
  }, [projects.length, hasMore, isLoading, onLoadMoreProjects])

  return (
    <div
      ref={containerRef}
      id="projectsScrollableDiv"
      className="flex-1 overflow-auto p-1 -m-1 tailwind-scroll"
    >
      <InfiniteScroll
        className={`p-1 -m-1 ${projectListGridClassName}`}
        scrollableTarget="projectsScrollableDiv"
        dataLength={projects?.length}
        next={onLoadMoreProjects}
        hasMore={hasMore}
        loader={Array.from({ length: 4 }).map((_, i) => (
          <Skeleton.Node
            key={i}
            className="w-full h-80"
            active
            style={{ width: "100%", height: "320px" }}
          />
        ))}
      >
        {projects?.map((project) => (
          <ProjectGridCard
            key={project.id}
            project={project}
            statusEditable={statusEditable}
            photoEditable={photoEditable}
            showProgress={showProgress}
            showBudget={showBudget}
            deleteAllowed={deleteAllowed}
            statuses={statuses}
            onClick={onProjectClick}
            onDelete={onDeleteProject}
            onStatusChange={onStatusChange}
            onUpdateMainPhoto={onUpdateMainPhoto}
          />
        ))}
      </InfiniteScroll>
    </div>
  )
}
